import vtkColorTransferFunction from 'vtk.js/Sources/Rendering/Core/ColorTransferFunction';
import { ColorSpace } from 'vtk.js/Sources/Rendering/Core/ColorTransferFunction/Constants';
import { ColorMode, ScalarMode } from 'vtk.js/Sources/Rendering/Core/Mapper/Constants';
import vtkLookupTable from 'vtk.js/Sources/Common/Core/LookupTable';
import vtkScalarBarActor from 'vtk.js/Sources/Rendering/Core/ScalarBarActor';
import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';
import vtkXMLPolyDataReader from 'vtk.js/Sources/IO/XML/XMLPolyDataReader';

import { logDebug, isInitialized, constants, ignore, getDUName, displayMapTypes, isNullOrUndefined } from '../js/Common';
import { setUpMapValuePicker, unsubscribe } from "../PickedMapValue/PickedMapValue";

const isDebugging = false;

function log(msg) {
    logDebug("Visualization.Meshes", isDebugging, msg);
}

//TODO - Update the logic below to be more robust, currently it'll break once we get to V1.10. See VRM-2510
function calculateVersion(rawVersion) {
    let major = rawVersion[0];
    let minor = rawVersion[1];
    let version = major + (minor / 10);

    return version;
}

/** @typedef {import('vtk.js/Sources/Rendering/Core/Renderer').vtkRenderer} vtkRenderer */
/**
 * @typedef { { dataSource: *, actor: vtkActor, mapper: vtkMapper } } Mesh
 */

/**
 * @typedef { { anatomy: Mesh, lumen: Mesh, lumenCenterline: Mesh, maps: Mesh, wallCenterline: Mesh, wall: Mesh, diameterRings: Mesh[], scalarBarActor: *, lookupTable: *, disposeMeshes(Meshes): void } } Meshes
 */

/** @typedef {import('../js/MeshVisualization.js').MapValuePickedArgs} MapValuePickedArgs */
/** @typedef {import('../js/MeshVisualization.js').onMapValuePicked} onMapValuePicked */
/** @typedef {import('../js/MeshVisualization.js').PickedMapValueProps} PickedMapValueProps */

function resolveRawContinuousName(wallPolyData) {
    const pd = wallPolyData.getPointData();
    if (pd.getArrayByName('RAW_Continuous')) return 'RAW_Continuous';
    if (pd.getArrayByName('RAW_SP_Continuous')) return 'RAW_SP_Continuous';
    return 'RAW_Continuous';
}

function resolveRawCellName(wallPolyData) {
    const cd = wallPolyData.getCellData();
    if (cd.getArrayByName('RAW')) return 'RAW';
    if (cd.getArrayByName('RAW_SP')) return 'RAW_SP';
    return 'RAW';
}

/**
 * Walks the wall polyData's triangles, producing per-triangle area (mm²) and sorted
 * RAW_Continuous values at the three vertices. Used as the static input for threshold-aware
 * coverage/area computation (see computeRawSummaryAt). Mesh data is static per-study so this
 * is computed once at load time.
 *
 * Triangles where any vertex's RAW value is NaN have their sorted v_min set to NaN, which
 * marks them as "no RAW data here" — they never contribute to coveredMm2 at any threshold.
 *
 * @param wallPolyData
 * @return {{ totalAreaMm2: number, triangles: Float64Array, hasRawData: boolean }}
 *   triangles layout: 4 floats per triangle = [area, v_min, v_mid, v_max].
 */
function computeRawTriangleStats(wallPolyData) {
    const points = wallPolyData.getPoints().getData();
    const polys = wallPolyData.getPolys().getData();
    const rawArrName = resolveRawContinuousName(wallPolyData);
    const rawArr = wallPolyData.getPointData().getArrayByName(rawArrName);
    const rawData = rawArr ? rawArr.getData() : null;

    // First pass: count triangles so we can allocate exactly.
    let triCount = 0;
    for (let i = 0; i < polys.length; ) {
        const n = Number(polys[i]); i += 1;
        if (n === 3) triCount += 1;
        i += n;
    }

    const triangles = new Float64Array(triCount * 4);
    let totalAreaMm2 = 0;
    let outIdx = 0;
    let i = 0;
    while (i < polys.length) {
        const n = Number(polys[i]); i += 1;
        if (n !== 3) { i += n; continue; }
        const a = Number(polys[i]); i += 1;
        const b = Number(polys[i]); i += 1;
        const c = Number(polys[i]); i += 1;
        const ax = points[3*a],     ay = points[3*a + 1], az = points[3*a + 2];
        const bx = points[3*b],     by = points[3*b + 1], bz = points[3*b + 2];
        const cx = points[3*c],     cy = points[3*c + 1], cz = points[3*c + 2];
        const ux = bx - ax, uy = by - ay, uz = bz - az;
        const vx = cx - ax, vy = cy - ay, vz = cz - az;
        const nx = uy*vz - uz*vy;
        const ny = uz*vx - ux*vz;
        const nz = ux*vy - uy*vx;
        const area = 0.5 * Math.sqrt(nx*nx + ny*ny + nz*nz);
        totalAreaMm2 += area;

        let va = rawData ? rawData[a] : NaN;
        let vb = rawData ? rawData[b] : NaN;
        let vc = rawData ? rawData[c] : NaN;
        if (Number.isNaN(va) || Number.isNaN(vb) || Number.isNaN(vc)) {
            // Mark the triangle as "no RAW data" — sentinel NaN in v_min.
            triangles[outIdx]     = area;
            triangles[outIdx + 1] = NaN;
            triangles[outIdx + 2] = NaN;
            triangles[outIdx + 3] = NaN;
        } else {
            // Sort va <= vb <= vc in place.
            if (va > vb) { const t = va; va = vb; vb = t; }
            if (vb > vc) { const t = vb; vb = vc; vc = t; }
            if (va > vb) { const t = va; va = vb; vb = t; }
            triangles[outIdx]     = area;
            triangles[outIdx + 1] = va;
            triangles[outIdx + 2] = vb;
            triangles[outIdx + 3] = vc;
        }
        outIdx += 4;
    }

    return {
        totalAreaMm2,
        triangles,
        hasRawData: rawData !== null,
    };
}

/**
 * Aggregates per-triangle stats into { rawCoveragePct, rawSurfaceAreaCm2 } at the given
 * threshold. Triangles fully above the threshold contribute their full area; triangles
 * straddling the threshold contribute their fractional area above (via linear iso-contour
 * interpolation along edges). Triangles with NaN vertex values never contribute.
 *
 * @param {{ totalAreaMm2: number, triangles: Float64Array }} stats
 * @param {number} threshold
 * @return {{ rawCoveragePct: number, rawSurfaceAreaCm2: number }}
 */
export function computeRawSummaryAt(stats, threshold) {
    const tris = stats.triangles;
    let coveredMm2 = 0;
    for (let i = 0; i < tris.length; i += 4) {
        const area = tris[i];
        const v0 = tris[i + 1];
        const v1 = tris[i + 2];
        const v2 = tris[i + 3];
        if (Number.isNaN(v0)) continue;                                    // no RAW data here
        if (v2 < threshold) continue;                                      // fully below
        if (v0 >= threshold) { coveredMm2 += area; continue; }             // fully above
        if (v1 >= threshold) {
            // v0 < threshold <= v1: portion above = triangle minus small corner near v0.
            const denom = (v1 - v0) * (v2 - v0);
            if (denom > 0) {
                const cutFrac = (threshold - v0) * (threshold - v0) / denom;
                coveredMm2 += area * (1 - cutFrac);
            }
        } else {
            // v1 < threshold <= v2: portion above is a small triangle near v2.
            const denom = (v2 - v0) * (v2 - v1);
            if (denom > 0) {
                coveredMm2 += area * ((v2 - threshold) * (v2 - threshold) / denom);
            }
        }
    }
    return {
        rawCoveragePct: stats.totalAreaMm2 > 0 ? (coveredMm2 / stats.totalAreaMm2) * 100 : 0,
        rawSurfaceAreaCm2: coveredMm2 / 100,
    };
}

/**
 * Reads the per-study VIGR score and bucket cutoffs from the wall mesh's FieldData. Returns
 * null if either array is missing (older datasets without VIGR FieldData).
 * @param wallPolyData
 * @return {{ score: number, cutoffs: [number, number] } | null}
 */
function readVigrStudyData(wallPolyData) {
    const fieldData = wallPolyData.getFieldData();
    if (!fieldData) return null;
    const vigrArr = fieldData.getArrayByName('VIGR');
    const cutoffsArr = fieldData.getArrayByName('VIGR_Cutoffs');
    if (!vigrArr || !cutoffsArr) return null;
    const score = vigrArr.getData()[0];
    const cutoffsData = cutoffsArr.getData();
    return {
        score,
        cutoffs: [cutoffsData[0], cutoffsData[1]],
    };
}

/**
 * Creates dataSources, actors, and mappers for all of the provided meshes.
 * @param studyMeshData
 * @returns Meshes
 */
export function mkMeshes(studyMeshData) {

    const loadMesh = meshData => {
        const vtpReader = vtkXMLPolyDataReader.newInstance();
        log("Parsing data with vtkXMLPolyDataReader");
        vtpReader.parseAsArrayBuffer(meshData);
        log("Parsed data");

        const lookupTable = vtkColorTransferFunction.newInstance();
        const dataSource = vtpReader.getOutputData();
        const mapper = vtkMapper.newInstance({
            interpolateScalarsBeforeMapping: false,
            useLookupTableScalarRange: true,
            lookupTable,
            scalarVisibility: false
        });
        mapper.setInputData(dataSource);

        const actor = vtkActor.newInstance();
        actor.setMapper(mapper);

        return {
            dataSource,
            actor,
            mapper
        };
    };

    const disposeMesh = mesh => {
        mesh.actor.delete();
        mesh.mapper.delete();
        mesh.dataSource.delete();
    };

    // There is no Map.map, so we have to improvise a little
    const loadedDiameterRings =
        new Map(
            Array
                .from(studyMeshData.diameterRings)
                .map(([key, value]) => {
                    const mesh = loadMesh(value);
                    mesh.actor.getProperty().setColor(0, 0, 1);
                    return ([key, mesh])
                }));

    const wallMapsMesh = loadMesh(studyMeshData.wallMaps);
    let cachedRawTriangleStats = null;
    const meshes = Object.freeze({
        anatomy: loadMesh(studyMeshData.anatomy),
        maybeDiametricGrowth: studyMeshData.maybeDiametricGrowth ? loadMesh(studyMeshData.maybeDiametricGrowth) : null,
        lumen: loadMesh(studyMeshData.lumen),
        lumenCenterline: loadMesh(studyMeshData.lumenCenterline),
        wallMaps: wallMapsMesh,
        wall: loadMesh(studyMeshData.wall),
        wallCenterline: loadMesh(studyMeshData.wallCenterline),
        renalArtery: loadMesh(studyMeshData.renalArtery),
        diameterRings: loadedDiameterRings,

        get rawTriangleStats() {
            if (cachedRawTriangleStats === null) {
                cachedRawTriangleStats = computeRawTriangleStats(wallMapsMesh.dataSource);
            }
            return cachedRawTriangleStats;
        },
        vigrStudyData: readVigrStudyData(wallMapsMesh.dataSource),
        rawContinuousArrayName: resolveRawContinuousName(wallMapsMesh.dataSource),
        rawCellArrayName: resolveRawCellName(wallMapsMesh.dataSource),

        scalarBarActor: vtkScalarBarActor.newInstance(),
        lookupTable: vtkLookupTable.newInstance(),
        colorTransFunc: vtkColorTransferFunction.newInstance(),

        displayed: [],

        disposeMeshes(meshes) {
            log("Disposing meshes");
            disposeMesh(meshes.anatomy);
            if (meshes.maybeDiametricGrowth) {
                disposeMesh(meshes.maybeDiametricGrowth);
            }
            disposeMesh(meshes.lumen);
            disposeMesh(meshes.lumenCenterline);
            disposeMesh(meshes.wallMaps);
            disposeMesh(meshes.wall);
            disposeMesh(meshes.wallCenterline);
            disposeMesh(meshes.renalArtery);
            meshes.diameterRings.forEach(disposeMesh);
            meshes.scalarBarActor.delete();
            meshes.lookupTable.delete();
            meshes.colorTransFunc.delete();
        }
    });

    const anatomyProperty = meshes.anatomy.actor.getProperty();
    anatomyProperty.setColor(1, 0, 0);

    const renalDataSource = meshes.renalArtery.dataSource;

    // TODO disable until VTK bug is fixed
    // https://gitlab.kitware.com/helpdesk/Prolucid/-/issues/1

    //let opacityArray = renalDataSource.getFieldData().getArrayByName(constants.dataArrayNames.renalOpacity).getTuple(0);
    //const opacity = isInitialized(opacityArray[0]) ? opacityArray[0] : 0.5;
    const opacity = 1.0;

    let colorArray = renalDataSource.getFieldData().getArrayByName(constants.dataArrayNames.renalColor).getTuple(0);
    if (!isInitialized(colorArray) || colorArray.length !== 3) {
        colorArray = [0.5, 0.5, 0.5];
    }

    const renalProperty = meshes.renalArtery.actor.getProperty();

    renalProperty.setColor(...colorArray);
    renalProperty.setOpacity(opacity);

    return meshes;
}

export function getDataFilesVersion(studyMeshData) {
    let rawVersion = [];
    let version = 0.0;
    let hasVersionNumber = studyMeshData.wallMaps.dataSource.getFieldData().hasArray(constants.dataArrayNames.analysisPipelineVersion);

    if (hasVersionNumber) {
        rawVersion = studyMeshData.wallMaps.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.analysisPipelineVersion).getTuple(0);
    }
    else {
        log("Failed to read Analysis Pipeline Version, using default version of 1.5");
        rawVersion = [1, 5, 0];
    }

    if (!isInitialized(rawVersion) || rawVersion.length !== 3) {
        rawVersion = [1, 5, 0];
    }
    version = calculateVersion(rawVersion);

    log("Using Analysis Pipeline Version: " + version);
    return version;
}

export function getAnalysisRevision(studyMeshData) {
    let version = "0";
    let hasVersionNumber = studyMeshData.wallMaps.dataSource.getFieldData().hasArray(constants.dataArrayNames.analysisRevision);

    if (hasVersionNumber) {
        const rawVersion = studyMeshData.wallMaps.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.analysisRevision).getTuple(0);
        version = isNullOrUndefined(rawVersion[0]) ? version : rawVersion[0].toString();
    }
    else {
        log("Analysis Revision not found");
    }

    log("Using Analysis Revision: " + version);
    return version;
}

export function getAcquisitionDate(studyMeshData) {
    let rawDate = [];
    let date = 0.0;
    let hasAcquisitionDate = studyMeshData.wallMaps.dataSource.getFieldData().hasArray(constants.dataArrayNames.acquisitionDate);

    if (hasAcquisitionDate) {
        rawDate = studyMeshData.wallMaps.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.acquisitionDate).getTuple(0);
    }
    else {
        log("Failed to read Acquisition Date, using default empty string");
        rawDate = [""];
    }

    if (!isInitialized(rawDate) || rawDate.length !== 1) {
        rawDate = [""];
    }

    date = rawDate[0];

    log("Using acquisition date: " + date);
    return date;
}

const vitaaColors = {
    nanColor: [0.5, 0.5, 0.5, 1],
    hueRange: [0.7, 0.1],
    rawHsvPoint1: [0.0, 0.7, 1.0, 1.0],
    rawHsvPoint2: [6.0, 0.20, 1.0, 1.0],
    rawHsvPoint3: [10.0, 0.01, 1.0, 1.0],
    rawKey: [0, 10],
    strainKey: [0, 0.15],
    iltKey: [0, 20],
    localExpansionGrowthKey: [0, 3],
    diametricGrowthKey: [0, 6],
    rawBelowThreshold: [0.45, 0.40, 0.32],
    vigrBarKey: [0, 30]
};

/**
 * @typedef {object} LookupTableConfigValue
 * @property {string} colorByArrayName
 * @property {number[]} dataRange
 * @property {string} legend
 * @property {number} scalarMode
 */

const lookupTableConfig = {
    /** @type LookupTableConfigValue */
    sectionalRaw: {
        colorByArrayName: "RAW_SP",
        dataRange: vitaaColors.rawKey,
        legend: "Sectional RAW",
        scalarMode: ScalarMode.USE_CELL_FIELD_DATA
    },
    /** @type LookupTableConfigValue */
    continuousRaw: {
        colorByArrayName: "RAW_SP_Continuous",
        dataRange: vitaaColors.rawKey,
        legend: "RAW",
        scalarMode: ScalarMode.USE_POINT_FIELD_DATA
    },
    /** @type LookupTableConfigValue */
    vigr: {
        colorByArrayName: "VIGR_Continuous",
        dataRange: vitaaColors.vigrBarKey,
        legend: "VIGR",
        scalarMode: ScalarMode.USE_POINT_FIELD_DATA
    },
    /** @type LookupTableConfigValue */
    localExpansionGrowth: {
        colorByArrayName: "Local_Growth",
        dataRange: vitaaColors.localExpansionGrowthKey,
        legend: "Local Expansion Growth (mm/year)",
        scalarMode: ScalarMode.USE_POINT_FIELD_DATA
    },
    /** @type LookupTableConfigValue */
    diametricGrowth: {
        colorByArrayName: "Maximum_Diameters",
        dataRange: vitaaColors.diametricGrowthKey,
        legend: "Diametric Growth (mm/year)",
        scalarMode: ScalarMode.USE_POINT_FIELD_DATA
    },
    /** @type LookupTableConfigValue */
    ilt: {
        colorByArrayName: "ILT",
        dataRange: vitaaColors.iltKey,
        legend: "ILT (mm)",
        scalarMode: ScalarMode.USE_POINT_FIELD_DATA
    },
    /** @type LookupTableConfigValue */
    strain: {
        colorByArrayName: "Strain",
        dataRange: vitaaColors.strainKey,
        legend: "Strain",
        scalarMode: ScalarMode.USE_POINT_FIELD_DATA
    }
};

export function getLookupTableConfigForSelectedMap(selectedMap, meshes) {
    switch (selectedMap) {
        case displayMapTypes.ANATOMY:
            return null;

        case displayMapTypes.CONTINUOUS_RAW:
            return {
                ...lookupTableConfig.continuousRaw,
                colorByArrayName: meshes?.rawContinuousArrayName ?? lookupTableConfig.continuousRaw.colorByArrayName,
            };

        case displayMapTypes.VIGR:
            return lookupTableConfig.vigr;

        case displayMapTypes.SECTIONAL_RAW:
            return {
                ...lookupTableConfig.sectionalRaw,
                colorByArrayName: meshes?.rawCellArrayName ?? lookupTableConfig.sectionalRaw.colorByArrayName,
            };

        case displayMapTypes.LOCAL_EXPANSION_GROWTH:
            return lookupTableConfig.localExpansionGrowth;

        case displayMapTypes.DIAMETRIC_GROWTH:
            return lookupTableConfig.diametricGrowth;

        case displayMapTypes.ILT:
            return lookupTableConfig.ilt;

        case displayMapTypes.STRAIN:
            return lookupTableConfig.strain;

        default:
            console.error(`Invalid map selected: ${selectedMap}`);
            return null;
    }
}

export function getMapDataAvailability(meshes) {
    const hasMap = (config, arrayName) => {
        try {
            const ds = meshes.wallMaps.dataSource;
            const attrs = config.scalarMode === ScalarMode.USE_CELL_FIELD_DATA
                ? ds.getCellData()
                : ds.getPointData();
            const name = arrayName ?? config.colorByArrayName;

            if (!attrs.hasArray(name)) {
                return false;
            }

            const [min, max] = attrs.getArrayByName(name).getRange();
            return min <= max && !(min === 0 && max === 0);
        } catch (e) {
            console.error("getMapDataAvailability failed; treating map as unavailable", e);
            return false;
        }
    };

    return {
        hasStrain: hasMap(lookupTableConfig.strain),
        hasIlt: hasMap(lookupTableConfig.ilt),
        hasContinuousRaw: hasMap(lookupTableConfig.continuousRaw, meshes.rawContinuousArrayName),
        hasVigr: meshes.vigrStudyData != null,
        rawContinuousArrayName: meshes.rawContinuousArrayName
    };
}

function buildLookupTable(lookupTable, dataRange, hueRange) {
    lookupTable.setMappingRange(...dataRange);
    lookupTable.setHueRange(...hueRange);
    lookupTable.setVectorModeToMagnitude();
    lookupTable.setNanColor(...vitaaColors.nanColor);
    lookupTable.build();
    return lookupTable;
}

function configureLookupTable(lookupTable, {
    colorByArrayName,
    dataRange,
    legend,
    scalarMode
}, mapper, scalarBarActor) {
    const interpolateScalarsBeforeMapping = false;
    const scalarVisibility = true;
    const colorMode = ColorMode.MAP_SCALARS;
    const lut = buildLookupTable(lookupTable, dataRange, vitaaColors.hueRange);

    mapper.set({
        colorByArrayName,
        colorMode,
        interpolateScalarsBeforeMapping,
        lookupTable: lut,
        scalarMode,
        scalarVisibility
    });

    if (scalarBarActor) {
        scalarBarActor.setAxisLabel(legend);
        scalarBarActor.setScalarsToColors(lookupTable);
    }
}

let rawColorSampler = null;
function getRawColorSampler() {
    if (rawColorSampler) return rawColorSampler;
    rawColorSampler = vtkColorTransferFunction.newInstance();
    rawColorSampler.setColorSpace(ColorSpace.HSV);
    rawColorSampler.addHSVPoint(...vitaaColors.rawHsvPoint1);
    rawColorSampler.addHSVPoint(...vitaaColors.rawHsvPoint2);
    rawColorSampler.addHSVPoint(...vitaaColors.rawHsvPoint3);
    rawColorSampler.build();
    return rawColorSampler;
}

function sampleOriginalRawColorAt(x) {
    const rgb = [0, 0, 0];
    getRawColorSampler().getColor(x, rgb);
    return rgb;
}

/**
 * Rebuilds the color transfer function so [0, threshold] is a flat grayish-brown plateau and
 * (threshold, 10] preserves the original RAW HSV gradient at original positions. The LUT's
 * mapping range stays at [0, 10] so the scalar bar never shrinks. A soft band of width
 * `bandWidth` around the threshold linearly interpolates between brown and the original RAW
 * color, so partially-crossed vertices show intermediate colors during animation. At rest pass
 * a near-zero bandWidth to restore a hard cutoff.
 * @param meshes
 * @param {number} threshold
 * @param {number} [bandWidth]
 */
export function applyRawThreshold(meshes, threshold, bandWidth = 1e-4) {
    const dataRange = vitaaColors.rawKey;
    const colorTransFunc = meshes.colorTransFunc;
    const hsvPoints = [vitaaColors.rawHsvPoint1, vitaaColors.rawHsvPoint2, vitaaColors.rawHsvPoint3];

    colorTransFunc.removeAllPoints();
    colorTransFunc.setMappingRange(...dataRange);
    colorTransFunc.setColorSpace(ColorSpace.HSV);
    colorTransFunc.setUseBelowRangeColor(false);

    if (threshold <= dataRange[0]) {
        for (const p of hsvPoints) colorTransFunc.addHSVPoint(...p);
    } else {
        const [br, bg, bb] = vitaaColors.rawBelowThreshold;
        const lo = Math.max(dataRange[0], threshold - bandWidth);
        const hi = Math.min(dataRange[1], threshold + bandWidth);
        colorTransFunc.addRGBPoint(dataRange[0], br, bg, bb);
        colorTransFunc.addRGBPoint(lo, br, bg, bb);
        const [tr, tg, tb] = sampleOriginalRawColorAt(hi);
        colorTransFunc.addRGBPoint(hi, tr, tg, tb);
        for (const p of hsvPoints) {
            if (p[0] > hi) colorTransFunc.addHSVPoint(...p);
        }
    }

    colorTransFunc.setNanColor(...vitaaColors.nanColor);
    colorTransFunc.build();
}

function buildHSVLookupTable(colorTransFunc, dataRange) {
    colorTransFunc.setMappingRange(...dataRange);
    colorTransFunc.setColorSpace(ColorSpace.HSV);
    colorTransFunc.addHSVPoint(...vitaaColors.rawHsvPoint1);
    colorTransFunc.addHSVPoint(...vitaaColors.rawHsvPoint2);
    colorTransFunc.addHSVPoint(...vitaaColors.rawHsvPoint3);
    colorTransFunc.setNanColor(...vitaaColors.nanColor);
    colorTransFunc.setUseBelowRangeColor(false);
    colorTransFunc.build();

    return colorTransFunc;
}

function configureHSVLookupTable(colorTransFunc, {
    colorByArrayName,
    dataRange,
    legend,
    scalarMode
}, mapper, scalarBarActor) {
    const interpolateScalarsBeforeMapping = false;
    const scalarVisibility = true;
    const colorMode = ColorMode.MAP_SCALARS;
    const lut = buildHSVLookupTable(colorTransFunc, dataRange);

    mapper.set({
        colorByArrayName,
        colorMode,
        interpolateScalarsBeforeMapping,
        lookupTable: lut,
        scalarMode,
        scalarVisibility
    });

    if (scalarBarActor) {
        scalarBarActor.setAxisLabel(legend);
        scalarBarActor.setScalarsToColors(lut);
    }
}
/**
 * autoLayout callback for vtkScalarBarActor that places the bar vertically on the right (vtk's
 * default for tall viewports) AND mirrors the resulting box/bar geometry back to the actor so
 * external consumers (the threshold slider overlay) can read the bar's true location. vtk's own
 * defaultAutoLayout writes only to the internal helper, leaving the actor's getters at their
 * unhelpful defaults.
 */
function verticalRightWithMirrorAutoLayout(helper) {
    const lastSize = helper.getLastSize();
    const xAxisAdjust = (lastSize[0] / 700) ** 0.8;
    const yAxisAdjust = (lastSize[1] / 700) ** 0.8;
    const minAdjust = Math.min(xAxisAdjust, yAxisAdjust);

    const axisTextStyle = helper.getAxisTextStyle();
    const tickTextStyle = helper.getTickTextStyle();
    axisTextStyle.fontSize = Math.max(24 * minAdjust, 12);
    tickTextStyle.fontSize = Math.max(20 * minAdjust, 10);

    const textSizes = helper.updateTextureAtlas();
    helper.setTickLabelPixelOffset(0.3 * tickTextStyle.fontSize);
    helper.setTopTitle(true);
    helper.setAxisTitlePixelOffset(0.2 * tickTextStyle.fontSize);

    const boxSize = helper.getBoxSizeByReference();
    boxSize[0] =
        (2.0 *
            (textSizes.tickWidth + helper.getTickLabelPixelOffset() + 0.8 * tickTextStyle.fontSize)) /
        lastSize[0];
    boxSize[1] = Math.max(1.2, Math.min(1.84 / yAxisAdjust, 1.84));
    const boxX = 0.98 - boxSize[0];
    const boxY = -0.92;
    helper.setBoxPosition([boxX, boxY]);

    helper.recomputeBarSegments(textSizes);

    const titleHeight = (2.0 * (textSizes.titleHeight + helper.getAxisTitlePixelOffset())) / lastSize[1];
    const tickWidth = (2.0 * (textSizes.tickWidth + helper.getTickLabelPixelOffset())) / lastSize[0];
    const actor = helper.getRenderable();
    if (actor) {
        actor.setBoxPosition(boxX, boxY);
        actor.setBoxSize(boxSize[0], boxSize[1]);
        actor.setBarPosition(boxX + tickWidth, boxY);
        actor.setBarSize(boxSize[0] - tickWidth, boxSize[1] - titleHeight);
    }
}

const VIGR_BAND_COLORS = {
    low: [1.00, 0.15, 0.10],     // vivid red (Low = high risk)
    medium: [1.00, 0.65, 0.00],  // pure orange
    high: [0.10, 0.85, 0.20],    // vivid green (High = low risk)
    none: vitaaColors.nanColor.slice(0, 3), // Neutral gray for studies with no VIGR field data
};
const VIGR_LOW_MEDIUM_THRESHOLD = 10;
const VIGR_MEDIUM_HIGH_THRESHOLD = 20;

/**
 * Classifies a study-level VIGR score into a risk bucket. Returns 'none' when the study carries
 * no VIGR field data (score/cutoffs absent).
 * @param {number|null|undefined} score
 * @param {[number, number]|null|undefined} cutoffs
 * @return {'none'|'low'|'medium'|'high'}
 */
export function classifyVigrScore(score, cutoffs) {
    if (score == null || !cutoffs) return 'none';
    if (score <= cutoffs[0]) return 'high';
    if (score <= cutoffs[1]) return 'medium';
    return 'low';
}

export function applyVigrSolidColor(meshes, vigrStudyData) {
    const bucket = classifyVigrScore(vigrStudyData?.score, vigrStudyData?.cutoffs);
    const [r, g, b] = VIGR_BAND_COLORS[bucket];
    meshes.wallMaps.mapper.set({
        colorByArrayName: null,
        colorMode: null,
        interpolateScalarsBeforeMapping: null,
        lookupTable: null,
        scalarMode: null,
        scalarVisibility: null,
    });
    meshes.wallMaps.actor.getProperty().setColor(r, g, b);
}

export function applyVigrBandedLut(meshes) {
    const dataRange = vitaaColors.vigrBarKey;
    const colorTransFunc = meshes.colorTransFunc;
    const [lr, lg, lb] = VIGR_BAND_COLORS.low;
    const [mr, mg, mb] = VIGR_BAND_COLORS.medium;
    const [hr, hg, hb] = VIGR_BAND_COLORS.high;

    colorTransFunc.removeAllPoints();
    colorTransFunc.setMappingRange(...dataRange);
    colorTransFunc.setColorSpace(ColorSpace.RGB);
    colorTransFunc.setUseBelowRangeColor(false);

    colorTransFunc.addRGBPoint(dataRange[0], hr, hg, hb);
    colorTransFunc.addRGBPoint(VIGR_LOW_MEDIUM_THRESHOLD - 1e-4, hr, hg, hb);
    colorTransFunc.addRGBPoint(VIGR_LOW_MEDIUM_THRESHOLD, mr, mg, mb);
    colorTransFunc.addRGBPoint(VIGR_MEDIUM_HIGH_THRESHOLD - 1e-4, mr, mg, mb);
    colorTransFunc.addRGBPoint(VIGR_MEDIUM_HIGH_THRESHOLD, lr, lg, lb);
    colorTransFunc.addRGBPoint(dataRange[1], lr, lg, lb);

    colorTransFunc.setNanColor(...vitaaColors.nanColor);
    colorTransFunc.build();
}

/**
 * Tick generator for the VIGR banded bar: replaces numeric ticks with "Low" / "Medium" / "High"
 * labels positioned at the mid-point of each band.
 */
function vigrBandGenerateTicks(helper) {
    const max = vitaaColors.vigrBarKey[1];
    helper.setTicks([
        VIGR_LOW_MEDIUM_THRESHOLD / 2,
        (VIGR_LOW_MEDIUM_THRESHOLD + VIGR_MEDIUM_HIGH_THRESHOLD) / 2,
        (VIGR_MEDIUM_HIGH_THRESHOLD + max) / 2,
    ]);
    helper.setTickStrings(['High', 'Medium', 'Low']);
}

/**
 * Tick generator that suppresses numeric tick labels on the bar (so the RAW slider's
 * numeric readout is the single source of truth for the current threshold value).
 */
function suppressBarTicks(helper) {
    helper.setTicks([]);
    helper.setTickStrings([]);
}

/**
 * Removes actors in meshes.displayed from renderer and clears meshes.displayed.
 * @param {vtkRenderer} renderer
 * @param {meshes} meshes
 */
export function removeDisplayedMeshes(renderer, meshes) {
    log(`Removing ${meshes.displayed.length} actors`);
    while (meshes.displayed.length > 0) {
        renderer.removeActor(meshes.displayed.pop());
    }
}

/**
 * Adds actors in meshes.displayed to renderer.
 * @param {vtkRenderer} renderer
 * @param {meshes} meshes
 * @param {boolean} isTransparent Optionally sets transparency on all displayed actors
 */
function addDisplayedMeshesToRenderer(renderer, meshes, isTransparent) {
    for (let i = 0; i < meshes.displayed.length; i++) {
        if (isTransparent) {
            meshes.displayed[i].getProperty().setOpacity(constants.meshOpacity);
        }
        renderer.addActor(meshes.displayed[i]);
    }
}

/**
 * Updates renderer's actors according to the specified selectedMap.
 * @param {string} meshViewportLocation
 * @param context
 * @param {string} selectedMap
 * @param {vtkRenderer} renderer
 * @param {meshes} meshes
 * @param {InteractionModeType} interactionMode
 * @param {PickedMapValueProps} pickedMapValueProps
 * @param {boolean} isPreviousOverlay Specifies whether meshes are for a previous study overlay
 * @param {boolean} isStentPlanning Specifies whether the selected map is for the stent planning viewport
 * @param {number} rawThreshold Threshold applied when the selected map is RAW; values below render as a flat brown plateau
 * @returns {void}
 */
export function updateSelectedMap(
    meshViewportLocation,
    context,
    selectedMap,
    renderer,
    meshes,
    interactionMode,
    pickedMapValueProps,
    isPreviousOverlay = false,
    isStentPlanning = false,
    rawThreshold = 0) {

    const { onMapValuePicked, clearPickedValues } = pickedMapValueProps || { onMapValuePicked: ignore, clearPickedValues: ignore };
    const { deletePickedIndicator } = context || { deletePickedIndicator: ignore };

    const replaceWithWallMaps = () => {

        log("Replacing anatomy with map");

        removeDisplayedMeshes(renderer, meshes);
        meshes.wallMaps.actor.getProperty().setOpacity(1.0);

        meshes.displayed.push(
            meshes.wallMaps.actor,
            meshes.scalarBarActor,
            meshes.renalArtery.actor
        );
        addDisplayedMeshesToRenderer(renderer, meshes, isPreviousOverlay);
    };

    const replaceWithAnatomy = () => {

        log("Replacing map with anatomy");

        removeDisplayedMeshes(renderer, meshes);
        const opactity = isStentPlanning ? constants.stentPlanningWallOpacity : constants.meshOpacity;
        meshes.wallMaps.actor.getProperty().setOpacity(opactity);
        if (isStentPlanning) {
            meshes.anatomy.actor.getProperty().setOpacity(constants.stentPlanningAnatomyOpacity);
            meshes.lumen.actor.getProperty().setOpacity(constants.stentPlanningAnatomyOpacity);
        }
        // Clear RAW mapper properties for anatomy
        meshes.wallMaps.mapper.set({
            colorByArrayName: null,
            colorMode: null,
            interpolateScalarsBeforeMapping: null,
            lookupTable: null,
            scalarMode: null,
            scalarVisibility: null,
        });

        let colorArray = meshes.wallMaps.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.anatomyColor).getTuple(0);
        if (!isInitialized(colorArray) || colorArray.length !== 3) {
            colorArray = [1, 1, 0];
        }
        if (isStentPlanning) colorArray = [0, 0, 0];
        meshes.wallMaps.actor.getProperty().setColor(...colorArray);
        meshes.displayed.push(
            meshes.wallMaps.actor,
            meshes.anatomy.actor
        );
        addDisplayedMeshesToRenderer(renderer, meshes, isPreviousOverlay);
    };

    const replaceWithDiametricGrowth = () => {
        if (meshes.maybeDiametricGrowth) {
            log("Replacing map with Diametric Growth");

            removeDisplayedMeshes(renderer, meshes);
            meshes.wallMaps.actor.getProperty().setOpacity(constants.meshOpacity);

            // Clear RAW mapper properties for Diametric Growth
            meshes.wallMaps.mapper.set({
                colorByArrayName: null,
                colorMode: null,
                interpolateScalarsBeforeMapping: null,
                lookupTable: null,
                scalarMode: null,
                scalarVisibility: null,
            });

            meshes.wallMaps.actor.getProperty().setColor([1, 1, 1]);
            meshes.wallMaps.actor.getProperty().setOpacity(0.3);

            meshes.displayed.push(
                meshes.scalarBarActor,
                meshes.renalArtery.actor,
                meshes.wallMaps.actor,
                meshes.maybeDiametricGrowth.actor
            );
            addDisplayedMeshesToRenderer(renderer, meshes, isPreviousOverlay);
        }
    };

    const setUpMapValuePickerWith = (lookupTableConfigValue) => {
        setUpMapValuePicker(
            lookupTableConfigValue,
            renderer,
            meshes,
            interactionMode,
            onMapValuePicked,
            meshViewportLocation,
            selectedMap,
            context);
    }

    unsubscribe(context);
    clearPickedValues(String(meshViewportLocation));
    deletePickedIndicator();

    meshes.scalarBarActor.resetGenerateTicksToDefault();
    meshes.scalarBarActor.resetAutoLayoutToDefault();
    meshes.scalarBarActor.setDrawNanAnnotation(true);

    switch (selectedMap) {
        case displayMapTypes.ANATOMY:
            // Don't show previous maps for anatomy
            if (!isPreviousOverlay) {
                replaceWithAnatomy();
            }
            break;

        case displayMapTypes.SECTIONAL_RAW: {
            const cfg = getLookupTableConfigForSelectedMap(selectedMap, meshes);
            replaceWithWallMaps();
            configureHSVLookupTable(meshes.colorTransFunc,
                cfg,
                meshes.wallMaps.mapper,
                meshes.scalarBarActor);

            setUpMapValuePickerWith(cfg);
            break;
        }

        case displayMapTypes.CONTINUOUS_RAW: {
            const cfg = getLookupTableConfigForSelectedMap(selectedMap, meshes);
            replaceWithWallMaps();
            meshes.scalarBarActor.setAutoLayout(verticalRightWithMirrorAutoLayout);
            meshes.scalarBarActor.setGenerateTicks(suppressBarTicks);
            meshes.scalarBarActor.setDrawNanAnnotation(false);
            configureHSVLookupTable(meshes.colorTransFunc,
                cfg,
                meshes.wallMaps.mapper,
                meshes.scalarBarActor);
            applyRawThreshold(meshes, rawThreshold);

            setUpMapValuePickerWith(cfg);
            break;
        }

        case displayMapTypes.VIGR:
            replaceWithWallMaps();
            meshes.scalarBarActor.setAutoLayout(verticalRightWithMirrorAutoLayout);
            meshes.scalarBarActor.setGenerateTicks(vigrBandGenerateTicks);
            meshes.scalarBarActor.setDrawNanAnnotation(false);
            applyVigrBandedLut(meshes);
            meshes.scalarBarActor.setAxisLabel(lookupTableConfig.vigr.legend);
            meshes.scalarBarActor.setScalarsToColors(meshes.colorTransFunc);
            applyVigrSolidColor(meshes, meshes.vigrStudyData);
            break;

        case displayMapTypes.LOCAL_EXPANSION_GROWTH:
            // Don't show previous maps for growth
            if (!isPreviousOverlay) {
                replaceWithWallMaps();
                configureLookupTable(meshes.lookupTable,
                    lookupTableConfig.localExpansionGrowth,
                    meshes.wallMaps.mapper,
                    meshes.scalarBarActor);
                setUpMapValuePickerWith(lookupTableConfig.localExpansionGrowth);
            }
            break;

        case displayMapTypes.DIAMETRIC_GROWTH:
            // Don't show previous maps for growth
            if (!isPreviousOverlay && meshes.maybeDiametricGrowth) {
                replaceWithDiametricGrowth();
                configureLookupTable(meshes.lookupTable,
                    lookupTableConfig.diametricGrowth,
                    meshes.maybeDiametricGrowth.mapper,
                    meshes.scalarBarActor);
                setUpMapValuePickerWith(lookupTableConfig.diametricGrowth);
            } else {
                log("Failed to replace map with Diametric Growth; Diametric Growth is null or undefined");
            }
            break;

        case displayMapTypes.ILT:
            // Don't show previous maps for ILT
            if (!isPreviousOverlay) {
                replaceWithWallMaps();
                configureLookupTable(meshes.lookupTable,
                    lookupTableConfig.ilt,
                    meshes.wallMaps.mapper,
                    meshes.scalarBarActor);
                setUpMapValuePickerWith(lookupTableConfig.ilt);
            }
            break;

        case displayMapTypes.STRAIN:
            replaceWithWallMaps();
            configureLookupTable(meshes.lookupTable,
                lookupTableConfig.strain,
                meshes.wallMaps.mapper,
                meshes.scalarBarActor);
            setUpMapValuePickerWith(lookupTableConfig.strain);
            break;

        default:
            console.error(`Invalid map selected: ${selectedMap}`);
            break;
    }
}
